import React, { useEffect } from 'react';
import { BodyS, BodySBold, BodyMBold, BodyM } from 'components/Typography/Typography';
import styles from 'assets/jss/material-dashboard-pro-react/views/numberPlanCardStyle';
import { Button, Card, makeStyles } from '@material-ui/core';
import DiscountImage from 'assets/img/discount.png';
import { BWODCardHeading } from './BWODCardHeading';
import { LINK_ACTIONS, STATUS, color, status } from './constants';
import { dynamicTranslate, translate } from 'components/Translation';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import BWODPlanDetails from './BWODPlanDetails';

import { daysLeftForExpiry, getCommonData, sortingOrder } from './helper';
import { compare } from 'utils/helpers';
import { useSelector } from 'react-redux';
import { getBwodLabels, getCommonLabels } from 'redux/modules/dynamicLabels/selectors';
import { getLanguage } from 'utils/language/language';
import GridItem from 'components/Grid/GridItem';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { InfoOutlined } from '@material-ui/icons/';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IBWODInfo {
	setBannerText: Function;
	setBannerBottomText: Function;
	openExtensionModal: Function;
	setBwodPlanExtend: Function;
	bwodresp: types.bwod.BWODResponse;
}

const BWODCard = ({
	setBannerText,
	setBannerBottomText,
	openExtensionModal,
	setBwodPlanExtend,
	bwodresp,
}: IBWODInfo) => {
	const [planCardInfo, setPlanCardInfo] = React.useState<{
		status: string;
		heading: string;
		buttonText?: string;
		footerHeading?: string;
		hideButton?: boolean;
		disableButton?: boolean;
		newPlanAvailable?: boolean;
	}>({ status: '', heading: '', buttonText: '', disableButton: false });
	const [bwodPlanDetails, setBwodPlanDetails] = React.useState<
		{
			planName?: string;
			body?: any[];
		}[]
	>([]);

	const [allPlansDetails, setAllPlanDetails] = React.useState<any>([]);
	const classes = useStyles({ border: () => color[planCardInfo?.status]?.border }); // the border of the card depends on the status of the card, border color is derieved at runtime
	const bwodLabels = useSelector(getBwodLabels);
	const commonLabels = useSelector(getCommonLabels);
	useEffect(() => {
		if (bwodresp) {
			const existingService = bwodresp.existing;
			const newService = bwodresp.recommendation;
			const expiredService = bwodresp.expired;
			//when customer hasn't used any service and is eligible for 1 or 2 months based on the validity
			if (!expiredService && !existingService && newService) {
				//isEligibleFor2Months checks id the customer is eligible for 2 months then we display the translation accordingly
				const isEligibleFor2Months =
					newService.find((plan: types.bwod.IService) => plan.proposedDurationMonths == '2') ||
					newService.length === 2;
				setPlanCardInfo({
					status: status.NEW,
					buttonText: bwodLabels.tryItNow,
					heading: bwodLabels.twoMonthEliHeading,
					newPlanAvailable: true,
				});
				//setting the details for the recommendation customer cad, which has plan name and plan details in the body
				const details = {
					planName: `${newService[0].productName} ${newService[0].downloadSpeed}`,
					body: [
						{
							key: bwodLabels.speedUpPrice,
							value: dynamicTranslate(
								bwodLabels.speedUpPriceValue,
								newService?.[0].activationCost,
								newService?.[0].vat
							),
						},
						{
							key: bwodLabels.validity,
							value: isEligibleFor2Months ? bwodLabels.twoMonthValidity : bwodLabels.oneMonthValidity,
						},
					],
				};
				setBwodPlanDetails([details]);
			} else {
				let mergedServices: any = [];
				//merging the objects of existing and expired services in one object
				if (existingService && expiredService) {
					mergedServices = [...existingService];
				} else if (existingService) {
					mergedServices = [...existingService];
				} else if (expiredService) {
					mergedServices = [...expiredService];
				}

				//sorting the services in the order of status - Active, Pending and Expired, so that it's rendered properly
				const sortedServices = mergedServices.sort(compare(STATUS, '', sortingOrder));

				//If there are more than 1 active service then calculate if the no of days left for each active service.
				const daysLeftArray = sortedServices.map((service: any) => {
					if (service.status == status.ACTIVE) {
						return daysLeftForExpiry(service.endDate);
					}
				});

				// This is to check if the status of service is active and exipry date of any active service is in then ext 5 days
				const showExpiryHeadsup = daysLeftArray.some((days: number) => days > -1 && days < 5);
				//no of days left
				const daysLeft = daysLeftArray.find((days: number) => days > -1 && days < 5);
				//show banner if expiry of active service is in next 5 days
				if (showExpiryHeadsup) {
					setBannerText({
						description: dynamicTranslate(bwodLabels.expiryDays, daysLeft),
					});
				}

				// We can have max of 2 services after merging the services: so putting the status in the array so that we can etxract details based on statuses combination
				let sevriceStatus: any[] = [];
				sortedServices.map((services: any) => sevriceStatus.push(services.status));
				if (sevriceStatus.includes(status.PENDING)) setBannerBottomText(bwodLabels.followPending);
				const data: any = getCommonData(
					sevriceStatus,
					bwodLabels,
					newService ? newService?.length : 0,
					existingService
				);
				setAllPlanDetails(sortedServices);
				if (expiredService?.length) {
					// if a plan is expired and there are no existing services then we show the expired card with extend now option and a banner on the top.
					if (!existingService) {
						//when customer is eligible for mone month and expired for one month
						setBannerText({
							description: bwodLabels.boosterExpired,
							link: bwodLabels.extendNow,
							action: LINK_ACTIONS.EXTEND_PLAN,
						});
						setPlanCardInfo(data);
					}
				}
				setPlanCardInfo(data);
			}
		}
	}, [bwodresp, getLanguage()]);

	const getButton = (text: string, disable: boolean, newPlanAvailable: boolean) => {
		return (
			<Button
				variant="contained"
				color="primary"
				size="small"
				data-testid="extendButton"
				className={classes.button}
				disabled={disable}
				onClick={() => {
					if (newPlanAvailable) {
						if (text === bwodLabels.extendNow) {
							setBwodPlanExtend(true);
						}
						openExtensionModal(true);
					}
				}}
			>
				{text}
			</Button>
		);
	};
	const getAddonSpeed = () => {
		switch (planCardInfo.status.toLowerCase()) {
			case status.ACTIVE.toLowerCase(): {
				const activeService = bwodresp.existing?.find(
					(service) => service.status.toLowerCase() === status.ACTIVE.toLowerCase()
				);
				return activeService?.downloadSpeed;
			}
			case status.EXPIRED.toLowerCase():
				return bwodresp.expired?.[0].downloadSpeed || '';
			default:
				return '';
		}
	};
	if (!planCardInfo?.status) return null;
	return (
		<Card className={classes.cardWrapper} data-testId="bwodCard">
			<BWODCardHeading
				heading={dynamicTranslate(planCardInfo.heading, getAddonSpeed() || '')}
				imageURL={DiscountImage}
				background={color[planCardInfo.status]?.background}
			/>
			<div className={classes.planDetailsWrapper}>
				{allPlansDetails.length <= 1 && (
					<BodyMBold customClassName={classes.caption}>{bwodLabels.addOn}</BodyMBold>
				)}
				{planCardInfo.status === status.NEW ? (
					<NewPlanDetails
						bwodPlanDetails={bwodPlanDetails[0].body || []}
						planName={bwodPlanDetails[0].planName || ''}
					/>
				) : (
					<>
						<BWODPlanDetails planStatus={planCardInfo.status} allPlansDetails={allPlansDetails} />
					</>
				)}
			</div>
			<ConditionalRender
				show={!!planCardInfo.footerHeading}
				onTrue={() => (
					<div className={classes.infoMessageContainer}>
						<div className={classes.infoiconWrapper}>
							<InfoOutlined />
						</div>
						<div className={classes.messageWrapper}>
							<BodyM disableMargin>{planCardInfo.footerHeading}</BodyM>
						</div>
					</div>
				)}
			/>
			{planCardInfo.buttonText &&
				!planCardInfo.hideButton &&
				getButton(
					planCardInfo.buttonText || '',
					planCardInfo.disableButton || false,
					planCardInfo.newPlanAvailable || false
				)}
		</Card>
	);
};

export default BWODCard;

const NewPlanDetails = ({ bwodPlanDetails, planName }: { bwodPlanDetails: any[]; planName: string }) => {
	const classes = useStyles({ border: () => {} });

	return (
		<>
			<div className={classes.newPlanDetails}>
				<BodyMBold>{planName}</BodyMBold>
				{bwodPlanDetails && bwodPlanDetails.length
					? bwodPlanDetails.map((item: any, index: number) => {
							return (
								<div key={index} className={classes.newPlanData}>
									<CheckCircleOutlineIcon fontSize="small" />
									<BodySBold customClassName={classes.planKey}>{item.key}</BodySBold>
									<BodyS>{item.value}</BodyS>
								</div>
							);
					  })
					: null}
			</div>
		</>
	);
};
